import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, from, map, mergeMap, Observable, of, reduce } from 'rxjs';
import type { Instance } from 'larvitar/dist/imaging/types';
import { DicomUploadClientService } from './dicom-upload-client.service';
import { SendingStatus } from './sending-status';
import { StowrsInputElement } from './stowrs-input-element';
import { StowrsResponse } from './stowrs-response';
import { PatientDetails } from '../../models/dto/patient-details';

export type RawRequest = { id: string; values: Array<StowrsInputElement>; patientDetails: PatientDetails };
export type PartialSendingStatus = { id: string; sendingStatus: SendingStatus };

@Injectable({
    providedIn: 'root',
})
export class DicomUploadService {
    public showSpinner$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    inProgress = 0;
    concurrentCalls = 2;
    multipartMaxFiles = 10;

    constructor(readonly clientService: DicomUploadClientService) {
        sessionStorage.setItem('maxConcurrentCalls', this.concurrentCalls.toString());
        sessionStorage.setItem('multipartMaxFiles', this.multipartMaxFiles.toString());
    }

    sendInstances(
        instances: Array<StowrsInputElement>,
        patientDetails: PatientDetails,
        isVna: boolean,
    ): Observable<Map<string, SendingStatus[]>> {
        const allInstances = this.prepareAllStowrsInputElement(instances, patientDetails);
        console.log('Blocchi da inviare: ', allInstances);
        return this.executeParallelCalls(allInstances, isVna);
    }

    private prepareAllStowrsInputElement(
        toSend: Array<StowrsInputElement>,
        patientDetails: PatientDetails,
    ): Array<RawRequest> {
        this.concurrentCalls = parseInt(sessionStorage.getItem('maxConcurrentCalls'));
        const studies: Map<string, Array<Array<StowrsInputElement>>> = this.partitionStowrsInputElement(toSend);
        const result = new Array();
        [...studies.keys()].forEach((k) =>
            studies.get(k)!.forEach((s) =>
                result.push({
                    id: k,
                    values: s,
                    patientDetails: patientDetails,
                }),
            ),
        );
        return result;
    }

    private executeParallelCalls(
        instances: Array<RawRequest>,
        isVna: boolean,
    ): Observable<Map<string, SendingStatus[]>> {
        console.log('ExecuteParallelaCalls .... executing');
        return from(instances).pipe(
            mergeMap((instance: RawRequest) => {
                return this.sendPartitionInstaces(instance, isVna).pipe(
                    map((k: StowrsResponse): PartialSendingStatus => {
                        const r = this.containsErrors(k);
                        const response = {
                            id: instance.id,
                            sendingStatus: new SendingStatus(false, r, true),
                        };
                        return response;
                    }),
                    catchError((e) => {
                        return of({
                            id: instance.id,
                            sendingStatus: new SendingStatus(true, false, false),
                        });
                    }),
                );
            }, this.concurrentCalls),
            reduce((acc: Map<string, SendingStatus[]>, one: PartialSendingStatus) => {
                if (!acc.has(one.id)) {
                    acc.set(one.id, []);
                }
                acc.get(one.id)!.push(one.sendingStatus);
                console.log('MAPPA con result per study: ', acc);
                return acc;
            }, new Map<string, SendingStatus[]>()),
        );
    }

    private sendPartitionInstaces(instances: RawRequest, isVna: boolean): Observable<StowrsResponse> {
        let input: Array<ArrayBuffer> = this.extractFiles(instances.values.map((e) => e.body));
        if (++this.inProgress === 1) {
            this.showSpinner$.next(true);
        }
        return this.clientService.upload(input, instances.patientDetails, isVna).pipe(
            finalize(() => {
                if (--this.inProgress === 0) {
                    this.showSpinner$.next(false);
                }
            }),
        );
    }

    private extractFiles(instances: Array<Instance>): Array<ArrayBuffer> {
        let tempFiles: Array<ArrayBuffer> = [];
        instances.forEach((item: Instance) => {
            tempFiles.push(item.dataSet.byteArray.buffer);
        });
        return tempFiles;
    }

    private containsErrors(e: StowrsResponse): boolean {
        return e ? this.containsFailedSequences(e) : true;
    }

    private containsFailedSequences(e: StowrsResponse): boolean {
        return !!e['00081198'] && e['00081198'].Value && e['00081198'].Value.length > 0;
    }

    private partitionStowrsInputElement(
        toSend: Array<StowrsInputElement>,
    ): Map<string, Array<Array<StowrsInputElement>>> {
        if (!toSend || toSend.length === 0) {
            return new Map();
        }
        return toSend.reduce((acc: Map<string, Array<Array<StowrsInputElement>>>, cur: StowrsInputElement) => {
            if (!acc.has(cur.studyId)) {
                acc.set(cur.studyId, [[]]);
            }
            let currentStudyElements: Array<Array<StowrsInputElement>> = acc.get(cur.studyId)!;
            let curSlice: Array<StowrsInputElement> = currentStudyElements[currentStudyElements?.length - 1];
            let lastElement: StowrsInputElement = curSlice[curSlice.length - 1];
            if (
                (!!lastElement && lastElement.seriesId !== cur.seriesId) ||
                curSlice.length >= this.maxLengthPerModality(cur.modality)
            ) {
                currentStudyElements.push([cur]);
            } else {
                curSlice.push(cur);
            }
            return acc;
        }, new Map<string, Array<Array<StowrsInputElement>>>());
    }

    // CT o CT+SR PT RM NM max di 5 alla volta, tutto il resto di 1
    private maxLengthPerModality(modality: string): number {
        if (
            modality.startsWith('CT') ||
            modality.startsWith('TC') ||
            modality.startsWith('PT') ||
            modality.startsWith('RM') ||
            modality.startsWith('MR') ||
            modality.startsWith('NM')
        ) {
            return parseInt(sessionStorage.getItem('multipartMaxFiles'));
        }
        this.concurrentCalls = 1;
        return 1;
    }
}
